import {Injectable} from '@angular/core';
import {RequesterService} from "../../../../shared/services/requester/requester.service";
import {BehaviorSubject} from "rxjs";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../store';
import {DbStoreState} from '../../../../store';
import {HostingService} from "../../hosting/hosting.service";
import {filter, map} from "rxjs/operators";
import {Router} from "@angular/router";
import {DbModalService} from "../../../../modules/modal/modal";
import {UserSubscriptionType} from "../../../../user/user-subscription";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {CacheRules} from "../mappers/cloudflare/cache-rules";
import {BandwidthData} from "../mappers/cloudflare/bandwidth";
import {DefaultRecord} from "../mappers/cloudflare/default-record";
import {CacheRulesServerParams} from "../mappers/cloudflare/cache-rule-server-params";
import {CloudflareData} from "../mappers/cloudflare/cloudflare-data";


@Injectable({
  providedIn: 'root'
})
export class CloudflareService {

  private _ownerWorkspaceId: number;
  private _currentWorkspaceId: number;
  private _isPaid: boolean;

  constructor(
    private _requester: RequesterService,
    private _hostingService: HostingService,
    private _modalService: DbModalService,
    private _dialogService: DialogService,
    private _router: Router,
    private _store: Store<DbStoreState>,
    private _googleAnalyticsService: GoogleAnalyticsService,
  ) {
    this._getWorkspaceEntities();
    this._getUserSubscription();
  }

  private _getWorkspaceEntities(): void {
    this._store
      .select(fromStore.getWorkspaceIdEntities)
      .subscribe(workspaceIdEntities => {
        this._ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
        this._currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this._ownerWorkspaceId;
      });
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserSubscription)
      .pipe(
        filter(subscription => !!subscription)
      )
      .subscribe(userSubscription => this._isPaid = userSubscription.type !== UserSubscriptionType.FREE);
  }


  public getCloudflareDate(websiteId: number, withCacheData: boolean = true): BehaviorSubject<DbResponse> {
    const params = withCacheData ? {with_cache_rules: 1} : null;
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/data`, params, true)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = new CloudflareData(response.data, withCacheData);
          }
          return response;
        })
      );
  }

  public getCacheRules(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/cache-rules`, null, null)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = response.data && response.data.cache_rules && new CacheRules(response.data.cache_rules);
          }
          return response;
        })
      );
  }

  public addUpdateCacheRules(websiteId: number, cacheRuleData: CacheRules): BehaviorSubject<DbResponse> {
    const data = {
      cache_rules: new CacheRulesServerParams(cacheRuleData)
    };
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/cache-rules`, null, data);
  }

  public getWebsiteCDNBandwidth(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .get(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/get-bandwidth`, null, null)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = new BandwidthData(response.data.bandwidth);
          }
          return response;
        })
      );
  }

  public toggleCDN(websiteId: number, action: boolean): BehaviorSubject<DbResponse> {
    const data = {
      'action': !action ? 'enable' : 'disable' // 'enable' || 'disable',
    };
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/cdn/toggle`, null, data, true)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = response.data.action === 'enable';
          }
          return response;
        })
      );
  }

  public purgeCache(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/cache/purge`, null, null);
  }

  public cacheToggle(websiteId: number, action: boolean): BehaviorSubject<DbResponse> {
    const data = {
      'action': !action ? 'enable' : 'disable' // 'enable' || 'disable',
    };
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/cache/toggle`, null, data, true)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = response.data.action === 'enable';
          }
          return response;
        })
      );
  }

  public getDefaultDnsRecord(websiteId: number, recordType?: string): BehaviorSubject<DbResponse> {
    const data = {
      record_type: recordType || 'A'
    };
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/get-dns-record`, null, data)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = new DefaultRecord(response.data);
          }
          return response;
        })
      );
  }

  public disableCloudflare(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/disable`, null, null);
  }

  public editDnsRecord(websiteId: number, ip: string): BehaviorSubject<DbResponse> {
    const data = {
      ip: ip
    };
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .put(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/edit-dns-record`, null, data);
  }

  public enableCloudflare(websiteId: number, ip: string): BehaviorSubject<DbResponse> {
    const data = {
      ip: ip
    };
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/enable`, null, data)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = new CloudflareData(response.data, true);
          }
          return response;
        })
      );
  }

  public deleteCustomHostname(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .delete(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/custom-hostname`, null, null);
  }

  public checkSSLCertificate(websiteId: number): BehaviorSubject<DbResponse> {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .post(`workspaces/${this._currentWorkspaceId}/domains/${websiteId}/cloudflare/certificate/check`, null, null);
  }

  public getBandwidth() {
    return <BehaviorSubject<DbResponse>>this._requester.lxd
      .get(`workspaces/${this._currentWorkspaceId}/cloudflare/get-bandwidth`, null, null)
      .pipe(
        map((response: DbResponse): DbResponse => {
          if (response && response.success) {
            response.data = new BandwidthData(response.data.bandwidth);
          }
          return response;
        })
      );
  }
}
